/**
 * Location 配置块 JSON Schema 定义
 */
export const locationSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/location.json',
  title: 'Location 配置',
  description: 'Location（地点）配置数据结构',
  type: 'object',
  required: ['location_id', 'name'],
  properties: {
    location_id: {
      type: 'string',
      title: 'Location ID',
      description: 'Location 唯一标识符',
      pattern: '^location_[a-zA-Z0-9_]+$',
      minLength: 10,
      maxLength: 50,
      examples: ['location_station_hall', 'location_market_square', 'location_secret_room'],
    },
    name: {
      type: 'string',
      title: '地点名称',
      description: '地点的显示名称',
      minLength: 1,
      maxLength: 100,
      examples: ['兵站大厅', '集市广场', '秘密房间'],
    },
    description: {
      type: 'string',
      title: '地点描述',
      description: '地点的详细描述',
      minLength: 0,
      maxLength: 1000,
    },
    location_type: {
      type: 'string',
      title: '地点类型',
      enum: ['室内', '室外', '地下', '水下', '空中', '其他'],
      default: '室内',
    },
    parent_location: {
      type: 'string',
      title: '父级地点',
      description: '所属的父级地点 ID',
      pattern: '^location_[a-zA-Z0-9_]+$',
    },
    connections: {
      type: 'array',
      title: '连接',
      description: '可通行的其他地点',
      items: {
        type: 'object',
        required: ['target_location_id'],
        properties: {
          target_location_id: {
            type: 'string',
            pattern: '^location_[a-zA-Z0-9_]+$',
          },
          direction: {
            type: 'string',
            title: '方向',
            enum: ['北', '南', '东', '西', '上', '下', '特殊通道'],
          },
          travel_time: {
            type: 'integer',
            title: '通行时间',
            description: '移动到该地点所需时间（秒）',
            minimum: 0,
          },
          locked: {
            type: 'boolean',
            title: '锁定',
            description: '是否需要解锁才能通过',
            default: false,
          },
          unlock_condition: {
            type: 'string',
            title: '解锁条件',
            description: '解锁所需的条件表达式',
          },
        },
      },
    },
    npcs: {
      type: 'array',
      title: 'NPC 列表',
      description: '该地点中存在的 NPC',
      items: {
        type: 'string',
        pattern: '^npc_[a-zA-Z0-9_]+$',
      },
    },
    items: {
      type: 'array',
      title: '物品列表',
      description: '该地点中可获取的物品',
      items: {
        type: 'string',
        pattern: '^item_[a-zA-Z0-9_]+$',
      },
    },
    events: {
      type: 'array',
      title: '事件列表',
      description: '该地点中可能触发的事件',
      items: {
        type: 'string',
        pattern: '^event_[a-zA-Z0-9_]+$',
      },
    },
    atmosphere: {
      type: 'string',
      title: '氛围',
      description: '地点的氛围描述',
      maxLength: 200,
      examples: ['阴暗恐怖', '温馨舒适', '庄严肃穆'],
    },
    lighting: {
      type: 'string',
      title: '光照',
      description: '地点的光照条件',
      enum: ['明亮', '昏暗', '黑暗', '自定义'],
    },
    sound_ambient: {
      type: 'string',
      title: '环境音效',
      description: '地点的环境音效资源路径',
      maxLength: 200,
    },
    background_music: {
      type: 'string',
      title: '背景音乐',
      description: '地点的背景音乐资源路径',
      maxLength: 200,
    },
    image: {
      type: 'string',
      title: '背景图片',
      description: '地点的背景图片资源路径',
      maxLength: 200,
    },
    capacity: {
      type: 'integer',
      title: '容量',
      description: '地点可容纳的最大人数',
      minimum: 1,
    },
    facilities: {
      type: 'array',
      title: '设施',
      description: '地点内的设施列表',
      items: {
        type: 'string',
        maxLength: 50,
        examples: ['休息区', '商店', '存档点', '传送点'],
      },
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type LocationConfig = {
  location_id: string
  name: string
  description?: string
  location_type?: string
  parent_location?: string
  connections?: Array<{
    target_location_id: string
    direction?: string
    travel_time?: number
    locked?: boolean
    unlock_condition?: string
  }>
  npcs?: string[]
  items?: string[]
  events?: string[]
  atmosphere?: string
  lighting?: string
  sound_ambient?: string
  background_music?: string
  image?: string
  capacity?: number
  facilities?: string[]
  metadata?: Record<string, any>
}
